import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

const BACKEND_URL = '';

export default function PaymentSuccessPage({ user }) {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const navigate = useNavigate();
  const polled = useRef(false);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId || polled.current) return;
    polled.current = true;
    pollStatus(sessionId, 0);
  }, [sessionId]);

  const pollStatus = async (sid, attempt) => {
    if (attempt >= 6) {
      setStatus('timeout');
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/stripe/checkout-status/${sid}`, {
        credentials: 'include',
      });
      const data = await res.json();

      if (data.payment_status === 'paid') {
        setStatus('paid');
        return;
      }
      if (data.status === 'expired') {
        setStatus('expired');
        return;
      }
      setTimeout(() => pollStatus(sid, attempt + 1), 2000);
    } catch {
      setTimeout(() => pollStatus(sid, attempt + 1), 2000);
    }
  };

  if (!sessionId) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation user={user} />
        <main className="pt-24 pb-12 px-6 flex items-center justify-center">
          <Card className="p-8 text-center max-w-md">
            <XCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Invalid Session</h2>
            <p className="text-slate-500 mb-6">No payment session found.</p>
            <Button onClick={() => navigate('/pricing')} className="bg-brand-navy text-white">
              Back to Pricing
            </Button>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />
      <main className="pt-24 pb-12 px-6 flex items-center justify-center">
        {status === 'checking' && (
          <Card data-testid="payment-checking" className="p-10 text-center max-w-md">
            <Loader2 className="h-16 w-16 text-brand-navy mx-auto mb-4 animate-spin" />
            <h2 className="text-xl font-bold mb-2">Processing Payment</h2>
            <p className="text-slate-500">Please wait while we confirm your payment...</p>
          </Card>
        )}

        {status === 'paid' && (
          <Card data-testid="payment-success" className="p-10 text-center max-w-md">
            <CheckCircle className="h-16 w-16 text-brand-sage mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2 text-slate-900">Payment Successful!</h2>
            <p className="text-slate-500 mb-8">
              Your premium access has been activated. Enjoy full access to courses and keynotes.
            </p>
            <div className="flex gap-3 justify-center">
              <Button
                data-testid="go-to-courses-btn"
                onClick={() => navigate('/courses')}
                className="bg-gradient-to-r from-brand-navy to-brand-sage text-white"
              >
                Browse Courses
              </Button>
              <Button
                data-testid="go-to-keynotes-btn"
                onClick={() => navigate('/keynotes')}
                variant="outline"
              >
                View Keynotes
              </Button>
            </div>
          </Card>
        )}

        {(status === 'expired' || status === 'timeout') && (
          <Card data-testid="payment-failed" className="p-10 text-center max-w-md">
            <XCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Payment {status === 'expired' ? 'Expired' : 'Status Unknown'}</h2>
            <p className="text-slate-500 mb-6">
              {status === 'expired'
                ? 'Your payment session has expired. Please try again.'
                : 'We could not confirm your payment. Please check your email for confirmation.'}
            </p>
            <Button onClick={() => navigate('/pricing')} className="bg-brand-navy text-white">
              Back to Pricing
            </Button>
          </Card>
        )}
      </main>
    </div>
  );
}
